import React from "react";
import { Modal } from "react-bootstrap";
import defaultImage from "../../Assets/fallbackimg_giftcard.png";
import { formatPrice, getImageUrl } from "../Utility/Utility";
import { getLineStatus, LINE_STATUS } from "./cartPricing";

/* Shown on Checkout when the cart has out of stock or unavailable lines.
   Lists the lines that will be left out of the order and the amount payable
   without them. Continue removes those lines from the cart, then checks out. Centred dialog on desktop, bottom sheet
   on mobile (see .cart-excluded-sheet in _Cart.scss). */
const CartExcludedItemsSheet = ({ show, lines, amountPayable, translation, onClose, onContinue, isContinuing }) => {
  const count = lines.length;
  const isOneItem = count === 1;
  const isOutOfStock = (line) => getLineStatus(line) === LINE_STATUS.OUT_OF_STOCK;
  const allOutOfStock = lines.every(isOutOfStock);
  const noneOutOfStock = !lines.some(isOutOfStock);
  // Out of stock and unavailable lines together: one combined title, and each row says which it is.
  const isMixed = !allOutOfStock && !noneOutOfStock;

  const titleTemplate = isMixed
    ? translation?.cartExcludedMixed
    : allOutOfStock
      ? isOneItem
        ? translation?.cartExcludedOutOfStockOne
        : translation?.cartExcludedOutOfStockMany
      : isOneItem
        ? translation?.cartExcludedUnavailableOne
        : translation?.cartExcludedUnavailableMany;
  const title = String(titleTemplate ?? "").replace("{count}", count);
  const message = isOneItem ? translation?.cartExcludedMessageOne : translation?.cartExcludedMessageMany;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="cart-excluded-sheet"
      contentClassName="cart-excluded-sheet__content"
      aria-labelledby="cart-excluded-sheet-title"
    >
      <div className="cart-excluded-sheet__handle" aria-hidden="true" />

      <div className="cart-excluded-sheet__header">
        <div className="cart-excluded-sheet__heading">
          <h2 id="cart-excluded-sheet-title" className="cart-excluded-sheet__title">
            {title}
          </h2>
          <p className="cart-excluded-sheet__message">{message}</p>
        </div>
        <button
          type="button"
          className="cart-excluded-sheet__close"
          aria-label={translation?.cartClose}
          onClick={onClose}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      <ul className="cart-excluded-sheet__list">
        {lines.map((line) => {
          const name = line.name || line.details?.name;
          return (
            <li className="cart-excluded-sheet__item" key={line.lineId}>
              <img
                className="cart-excluded-sheet__thumb"
                src={getImageUrl(line.productImage || line.details?.image) || defaultImage}
                alt={name || ""}
                onError={(e) => {
                  e.target.src = defaultImage;
                }}
              />
              <div className="cart-excluded-sheet__info">
                <span className="cart-excluded-sheet__name">{name}</span>
                <span className="cart-excluded-sheet__meta">
                  ₹{formatPrice(line.denomination)} · {translation?.qty} {line.quantity}
                  {isMixed && (
                    <> · {isOutOfStock(line) ? translation?.out_of_stock : translation?.cartUnavailable}</>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="cart-excluded-sheet__payable">
        <span className="cart-excluded-sheet__payable-label">{translation?.cartAmountPayable}</span>
        <span className="cart-excluded-sheet__payable-value">₹{formatPrice(amountPayable)}</span>
      </div>

      <button
        type="button"
        className="cart-btn cart-btn--primary cart-excluded-sheet__continue"
        onClick={onContinue}
        disabled={isContinuing}
      >
        {isContinuing ? (
          <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
        ) : (
          translation?.cartContinueToCheckout
        )}
      </button>
    </Modal>
  );
};

export default CartExcludedItemsSheet;
